import React from 'react';
import { X, ChevronRight, SkipForward, Check, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { StorybookPromptVoicePanel } from './StorybookPromptVoicePanel';
import { StorybookAnswerMetrics } from './StorybookAnswerMetrics';

export function StorybookInterviewMode({
  open,
  onClose,
  prompt,
  promptIndex,
  totalPrompts,
  draftBody,
  onDraftChange,
  voice,
  saving,
  saveStatus,
  onSave,
  onSaveAndNext,
  onSkip,
}) {
  if (!open || !prompt) return null;

  const canSave = Boolean(draftBody.trim()) || voice.recordingCount > 0;

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col bg-[hsl(42_28%_97%)]"
      data-testid="storybook-interview-mode"
    >
      <header className="flex items-center justify-between border-b border-[hsl(38_14%_88%)] px-4 py-3 sm:px-6">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-widest text-[#8b7355]">Interview Mode</p>
          <p className="text-xs text-muted-foreground tabular-nums">
            Question {promptIndex + 1} of {totalPrompts}
          </p>
        </div>
        <Button type="button" variant="ghost" size="icon" className="rounded-full" onClick={onClose} aria-label="Exit interview">
          <X className="h-5 w-5" />
        </Button>
      </header>

      <main className="mx-auto flex w-full max-w-2xl flex-1 flex-col gap-6 overflow-y-auto px-4 py-8 sm:px-6">
        <p className="font-serif text-2xl leading-relaxed text-[#1c1917] sm:text-3xl">
          {prompt.prompt_text}
        </p>

        <StorybookPromptVoicePanel voice={voice} compact />

        <div className="space-y-2">
          <Textarea
            value={draftBody}
            onChange={(e) => onDraftChange(e.target.value)}
            rows={6}
            placeholder="Your answer appears here after transcribing, or type directly…"
            className="min-h-[140px] font-serif text-base leading-relaxed border-[hsl(38_14%_86%)]"
          />
          <StorybookAnswerMetrics
            body={draftBody}
            completed={false}
            recordingCount={voice.recordingCount}
            transcriptStatusLabel={voice.transcriptionStatusLabel}
          />
          {saveStatus ? (
            <p className="text-[11px] text-muted-foreground">{saveStatus}</p>
          ) : null}
        </div>
      </main>

      <footer className="border-t border-[hsl(38_14%_88%)] bg-[hsl(42_28%_99%)] px-4 py-4 sm:px-6">
        <div className="mx-auto flex max-w-2xl flex-wrap gap-2">
          <Button
            type="button"
            className="rounded-full gap-1.5"
            disabled={saving || !canSave}
            onClick={onSaveAndNext}
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <ChevronRight className="h-4 w-4" />}
            Save &amp; next
          </Button>
          <Button type="button" variant="outline" className="rounded-full" disabled={saving || !canSave} onClick={onSave}>
            <Check className="mr-1 h-4 w-4" /> Save answer
          </Button>
          <Button type="button" variant="ghost" className="rounded-full gap-1" disabled={saving} onClick={onSkip}>
            <SkipForward className="h-4 w-4" /> Skip
          </Button>
          <Button type="button" variant="ghost" className="rounded-full ml-auto" onClick={onClose}>
            Exit interview
          </Button>
        </div>
      </footer>
    </div>
  );
}

export default StorybookInterviewMode;
